import React, { useMemo, useState } from 'react';

export default function EditPage() {
  // Dummy paragraphs
  const initialParas = [
    'Between me and the first interspecies trial lies a monorail, a marble staircase and a wooden door. Before crossing, I look out. Mist both ways. Can easily cut with a sword. Last thing I want is a silent maglev tram smashing me into a double patty burger.',
    'I cross the rail. Climb the stone. Push the wood. I’m in.',
    '“…humans against Q:NET, Marvin of the 1st generation. Prosecution can…,” I hear while stepping on a purple carpet in Tinghuset Oslo’s main courtroom.',
    'The door to the courthouse slams and swallows the rest. I start leaving dust behind me. Literally. The carpet needs deep cleaning. I reach the other edge. An airport security scanner.',
    'Behind the machine, a slim man in a blue shirt, long pants. Holds a pen, writes on paper. I park in front of his desk. He writes a 9 in the upper corner of a maze with rectangles. Still doesn’t look up.',
    'I go through the scanner. The machine beeps.',
    '“No tech is allowed in the court,” he says. On autopilot.',
    'I stand under. Wait. He writes an 8.',
    '«And?»',
    'Silence.',
    '"What do I do now?"',
    'He looks up. Scuffs. Drops the pencil on the desk. Disappears. Unfortunately, I have time. My mind solves the maze on his paper. I switch focus. I notice her majesty Justitia. Her scale stands for the door handle to the courtroom. Scent of freshly baked cinnamon buns pierces my nostrils. I hear metal trays cling under the desk. But I don’t hear the prosecution.',
    'My fingers begin to roll. On autopilot. I drill the scanner frame. It doesn’t complain now. I count along. On the seventh roll - the man pops back up with a tray. Puts it on the desk. Next to a stack of paper notebooks.',
    '«Here. Put any tech you might have on you in the tray and try again,”',
    'He sits behind the desk. Picks the pencil. Scratches his head. Any other day, I would complain - today - I  don’t have time. I want to watch the trial. I put my glasstab, my c:lens and my watch int the tray. Feed it into the scanner. Pass.',
  ];

  const [paras, setParas] = useState(initialParas);

  // Engagement metadata, mapped per paragraph
  const meta = useMemo(() => ([
    { label: 'High engagement', views: 18400, likes: 2200, trend: 'up',   isCharacter: false },
    { label: 'Flat',            views: 3200,  likes: 210,  trend: 'flat', isCharacter: true  },
    { label: 'Spike',           views: 40100, likes: 5100, trend: 'spike',isCharacter: false },
  ]), []);

  const colors = {
    surface: '#0f172a',
    surfaceAlt: '#111827',
    text: '#e5e7eb',
    sub: '#9aa0a6',
    border: 'rgba(255,255,255,0.08)',
    up: '#22c55e',
    flat: '#a3a3a3',
    spike: '#f59e0b',
  };

  const wrap = { maxWidth: 1056, margin: '0 0 0 6px', padding: 8, color: colors.text };
  const row = { display: 'grid', gridTemplateColumns: '1fr 280px', gap: 10, alignItems: 'stretch' };
  const editor = { background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 8, padding: 10, minHeight: 420 };
  const sidebar = { background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 8, padding: 10 };
  const h = { margin: 0, marginBottom: 8, fontSize: 14, color: colors.sub };

  const paraBox = { background: colors.surfaceAlt, border: `1px solid ${colors.border}`, borderRadius: 8, padding: 10, marginBottom: 8 };
  const textarea = { width: '100%', background: 'transparent', border: 'none', color: colors.text, resize: 'vertical', lineHeight: 1.5, fontSize: 14, outline: 'none' };
  const badges = { display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end', marginBottom: 6 };
  const badge = (bg) => ({ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(255,255,255,0.06)', border: `1px solid ${colors.border}`, padding: '2px 6px', borderRadius: 999, fontSize: 11, color: colors.text });

  const tinySpark = (c) => (<span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 999, background: c }} />);

  const TrendGlyph = ({ t }) => {
    if (t === 'up')   return <span title="High engagement" style={{ color: colors.up }}>▲</span>;
    if (t === 'flat') return <span title="Flat" style={{ color: colors.flat }}>▬</span>;
    return <span title="Spike" style={{ color: colors.spike }}>⬀</span>;
  };

  const format = (n) => n >= 1000 ? `${Math.round(n/100)/10}k` : String(n);

  // Heartbeat / story-beat underlines as inline SVG
  const BeatLine = ({ trend, highlight }) => {
    const w = 560; const h = 24; const mid = h / 2;
    const stroke = highlight ? '#c4b5fd' : (trend === 'up' ? colors.up : trend === 'flat' ? colors.flat : colors.spike);
    const opacity = 0.9;
    // three pattern options
    const pathUp = `M0 ${mid} L80 ${mid} 100 ${mid-6} 160 ${mid+3} 240 ${mid-8} 300 ${mid} 380 ${mid-10} 420 ${mid-2} 560 ${mid-2}`;
    const pathFlat = `M0 ${mid} L560 ${mid}`;
    const pathSpike = `M0 ${mid} L100 ${mid} 120 ${mid-10} 140 ${mid} 180 ${mid} 200 ${mid-12} 220 ${mid} 260 ${mid} 320 ${mid-14} 340 ${mid} 560 ${mid}`;
    const d = trend === 'up' ? pathUp : trend === 'flat' ? pathFlat : pathSpike;
    return (
      <svg width="100%" height={h} viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" style={{ marginTop: 6 }}>
        <path d={d} stroke={stroke} strokeWidth="2" fill="none" strokeOpacity={opacity} />
      </svg>
    );
  };

  const updatePara = (i, val) => {
    setParas((prev) => prev.map((p, idx) => (idx === i ? val : p)));
  };

  return (
    <div style={wrap}>
      <div style={row}>
        <div style={editor}>
          <h3 style={h}>Edit text</h3>
          {paras.map((p, i) => (
            <div key={i} style={paraBox}>
              <div style={badges}>
                {/* Social overlays per paragraph */}
                {meta[i]?.isCharacter && (
                  <span style={badge('#8b5cf6')} title="Tag">{tinySpark('#8b5cf6')} Character description</span>
                )}
                <span style={badge('#5865f2')} title="Views"><TrendGlyph t={meta[i]?.trend} /> {format(meta[i]?.views || 0)} views</span>
                <span style={{...badge('#ef4444'), fontWeight: meta[i]?.isCharacter ? 700 : 500}} title="Likes">{tinySpark('#ef4444')} {format(meta[i]?.likes || 0)} likes</span>
                <span style={badge('#60a5fa')} title="Channel">{tinySpark('#60a5fa')} IG/X/YT</span>
              </div>
              <textarea
                style={textarea}
                rows={Math.max(3, Math.ceil((p.length || 80) / 80))}
                value={p}
                onChange={(e) => updatePara(i, e.target.value)}
              />
              <BeatLine trend={meta[i]?.trend} highlight={meta[i]?.isCharacter} />
            </div>
          ))}
        </div>

        <aside style={sidebar}>
          <h3 style={h}>Social performance</h3>
          <div style={{ display: 'grid', gap: 8 }}>
            {meta.map((m, i) => (
              <div key={i} style={{ background: colors.surfaceAlt, border: `1px solid ${colors.border}`, borderRadius: 8, padding: 8, display: 'grid', gridTemplateColumns: 'auto 1fr auto', gap: 8, alignItems: 'center' }}>
                <TrendGlyph t={m.trend} />
                <div>
                  <div style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                    {['Paragraph 1','Paragraph 2','Paragraph 3'][i]}
                    {m.isCharacter && (
                      <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 999, border: `1px solid ${colors.border}`, background: 'rgba(139,92,246,0.15)', color: '#c4b5fd' }}>Character</span>
                    )}
                  </div>
                  <div style={{ fontSize: 11, color: colors.sub }}>{m.label}</div>
                </div>
                <div style={{ textAlign: 'right', fontSize: 12 }}>
                  <div>{format(m.views)} views</div>
                  <div style={{ color: colors.sub, fontWeight: m.isCharacter ? 600 : 400 }}>{format(m.likes)} likes</div>
                </div>
              </div>
            ))}
          </div>
        </aside>
      </div>
    </div>
  );
}
